import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import {Button, message, Space} from "antd";
import {CustomInputComponent} from "../../../components/input/input-component";
import {FullPageLoader} from "../../../components/full-page-loader";
import {StyledLink} from "../../../layouts/auth-form-wrapper/auth-form-wrapper.style";
import {ILoginFormValues} from "./i-login";
import {loginSchema} from "./login-form-validation-schema";
import {
  loginStandardUser,
  loginWithGoogleCallback,
  resetUserLoginState,
} from "../../../redux/auth/login/user-login-slice";
import {getUserProfile} from "../../../redux/profile/profile-slice";
import {store, AppDispatch, RootState} from "../../../redux/store";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {maskEmail} from "../../../utils/mask-email";
import {TwoFactorLoginModal} from "./components/TwoFactorLoginModal";
import {useResolvePostAuthRedirect} from "../hooks/use-resolve-post-auth-redirect";
import {IS_TWO_FACTOR_FEATURE_ENABLED} from "../../../services/config";

const LoginForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const dispatch = useDispatch<AppDispatch>();
  const {
    userInfo,
    isLoading: isLoadingUserLogin,
    isSuccess: isSuccessUserLogin,
    isError: isErrorUserLogin,
    message: userLoginMessage,
    twoFactorChallenge,
  } = useSelector((state: RootState) => state.userLogin);
  const {profileData} = useSelector((state: RootState) => state.userProfile);
  // Gated on isSuccessUserLogin so this doesn't fire an authenticated
  // request the moment the login page mounts, before anyone has logged in.
  const {resolvePostAuthRedirect, isKycStatusLoading} =
    useResolvePostAuthRedirect(isSuccessUserLogin);

  // If Google redirected back here with ?code=&state=, this page doubles as
  // the OAuth landing page — captured once so it doesn't re-trigger on the
  // re-render that strips the query params below.
  const [isGoogleCallbackPending, setIsGoogleCallbackPending] = useState(
    () => !!(searchParams.get("code") && searchParams.get("state"))
  );

  // Clear request state rehydrated by older builds that persisted the entire
  // login slice. Durable user information remains untouched.
  useEffect(() => {
    dispatch(resetUserLoginState());
  }, [dispatch]);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (code && state) {
      // Strip code/state from the URL immediately — state is single-use, so a
      // refresh or back-navigation must not resubmit it.
      navigate("/auth/login", {replace: true});
      dispatch(
        loginWithGoogleCallback({
          code,
          state,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isErrorUserLogin) {
      message.error(userLoginMessage);
      dispatch(resetUserLoginState());
      setIsGoogleCallbackPending(false);
    }
  }, [isErrorUserLogin, userLoginMessage, dispatch]);

  // A Google sign-in that lands on a 2FA challenge hands over to the modal.
  // Dropping the loader here means backing out of the modal reveals the form
  // instead of a spinner that nothing will ever clear.
  useEffect(() => {
    if (twoFactorChallenge.required) {
      setIsGoogleCallbackPending(false);
    }
  }, [twoFactorChallenge.required]);

  // Fetch profile after successful login
  useEffect(() => {
    if (isSuccessUserLogin && userInfo?.accessToken) {
      // Fetch profile data to check verification status
      dispatch(getUserProfile(null));
    }
  }, [isSuccessUserLogin, userInfo?.accessToken, dispatch]);

  // Reads fresh state via store.getState() rather than the closed-over
  // selector values. Only ever called from the effect below
  const handleAfterLogin = () => {
    const state = store.getState();
    const user = state.userLogin.userInfo?.user;

    if (IS_TWO_FACTOR_FEATURE_ENABLED && user && !user.twoFactorMethod) {
      // Nudge users who haven't set up 2FA yet before continuing. The KYC/
      // redirectTo decision resumes via useResolvePostAuthRedirect once the
      // nudge is resolved (see TwoFactorNudgePage).
      navigate(
        `/auth/2fa-setup${
          redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""
        }`
      );
      dispatch(resetUserLoginState());
      return;
    }

    resolvePostAuthRedirect(redirectTo);

    // Reset login state after redirect
    dispatch(resetUserLoginState());
  };

  // Handle redirect after profile is fetched. Also waits on the KYC status
  // query so handleAfterLogin doesn't read a not-yet-loaded isKycVerified
  // and wrongly bounce an already-verified coach to the KYC settings tab.
  useEffect(() => {
    if (
      isSuccessUserLogin &&
      profileData &&
      !isKycStatusLoading &&
      userInfo?.accessToken
    ) {
      handleAfterLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUserLogin, profileData, isKycStatusLoading, userInfo?.accessToken]);

  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
  } = useForm<ILoginFormValues>({
    resolver: yupResolver(loginSchema),
    mode: "onChange",
  });

  const onSubmit = async (values: ILoginFormValues) => {
    dispatch(
      loginStandardUser({...values, emailAddress: values.emailAddress.toLowerCase()})
    );
  };

  return (
    <>
      {isGoogleCallbackPending ? (
        <FullPageLoader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Space direction="vertical" size={29} style={{display: "flex"}}>
            <CustomInputComponent
              id="email"
              name="emailAddress"
              label="Email address"
              type="email"
              placeholder="Please enter your email address"
              error={errors.emailAddress?.message}
              control={control}
            />
            <CustomInputComponent
              id="password"
              name="password"
              label="Password"
              type="password"
              placeholder="•••••••••••••••"
              error={errors.password?.message}
              control={control}
            />
            <StyledLink to="/auth/forgot-password">Forgot password</StyledLink>
            <div>
              <Button
                disabled={
                  Object.keys(errors).length > 0 || Object.values(watch()).length < 1
                }
                block
                loading={isLoadingUserLogin}
                size="small"
                type="primary"
                htmlType="submit"
              >
                Log in
              </Button>
            </div>
            <p className="login-option">
              Bought something?{" "}
              <Link
                style={{textDecoration: "underline", fontWeight: 600}}
                to="/auth/customer/login"
              >
                Access your purchase here
              </Link>
            </p>
          </Space>
        </form>
      )}
      {/*
        Not gated on IS_TWO_FACTOR_FEATURE_ENABLED: a challenge only gets here
        when the backend demands one for this login. Stale challenges from the
        2FA rollback can't re-open it — the slice no longer persists them, and
        the v1 persist migration dropped any left on disk.
      */}
      {twoFactorChallenge.required && (
        <TwoFactorLoginModal
          visible={twoFactorChallenge.required}
          challengeToken={twoFactorChallenge.challengeToken!}
          method={twoFactorChallenge.method!}
          cooldownRemainingSeconds={twoFactorChallenge.cooldownRemainingSeconds}
          maskedEmail={
            watch("emailAddress") ? maskEmail(watch("emailAddress")) : "your email"
          }
        />
      )}
    </>
  );
};

export {LoginForm};
